import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { posApi } from '@/api/pos.api';
import type { PosHourlySales, PosTopProduct } from '@/api/pos.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { formatCurrency } from '@/utils/format';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { Skeleton } from '@/components/ui/Skeleton';
import { DatePicker } from '@/components/ui/DatePicker';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

function HourlySalesChart({
  data,
  currencyCode,
}: {
  data: PosHourlySales[];
  currencyCode: string;
}) {
  const maxSales = Math.max(...data.map((d) => d.totalSales), 1);

  const formatHour = (h: number) => {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}${ampm}`;
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="card p-5">
      <h2 className="section-title">Hourly sales</h2>
      <div className="mt-4 flex h-36 items-end gap-0.5 overflow-x-auto pb-2">
        {hours.map((hour) => {
          const bucket = data.find((d) => d.hour === hour);
          const sales = bucket?.totalSales ?? 0;
          const heightPct = maxSales > 0 ? (sales / maxSales) * 100 : 0;
          const isActive = sales > 0;

          return (
            <div key={hour} className="group relative flex flex-1 flex-col items-center">
              {isActive && (
                <div className="pointer-events-none absolute -top-12 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-2 py-1 text-xs text-white shadow-lg group-hover:block dark:bg-slate-700">
                  {formatHour(hour)}: {formatCurrency(sales, currencyCode)}
                  <br />
                  {bucket?.transactionCount ?? 0} txns
                </div>
              )}
              <div
                className={`w-full rounded-t transition-all duration-300 ${
                  isActive ? 'bg-primary-600 dark:bg-primary-500' : 'bg-slate-100 dark:bg-slate-800'
                }`}
                style={{ height: `${Math.max(heightPct, isActive ? 4 : 2)}%` }}
              />
              {hour % 4 === 0 && (
                <p className="mt-1 text-[9px] text-slate-400">{formatHour(hour)}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopProductsTable({
  products,
  currencyCode,
}: {
  products: PosTopProduct[];
  currencyCode: string;
}) {
  if (!products.length) {
    return (
      <div className="card p-5">
        <h2 className="section-title">Top products</h2>
        <p className="py-8 text-center text-sm text-slate-500">No sales data yet today</p>
      </div>
    );
  }

  const maxRev = products[0]?.revenue ?? 1;

  return (
    <div className="card p-5">
      <h2 className="section-title">Top products</h2>
      <div className="mt-4 space-y-3">
        {products.map((p, i) => (
          <div key={p.productId} className="flex items-center gap-3">
            <span className="w-5 text-center text-xs font-semibold text-slate-400">#{i + 1}</span>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{p.productName}</p>
                <p className="ml-4 shrink-0 text-sm font-semibold tabular-nums text-primary-600 dark:text-primary-400">
                  {formatCurrency(p.revenue, currencyCode)}
                </p>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className="h-full rounded-full bg-primary-600 transition-all duration-700"
                  style={{ width: `${(p.revenue / maxRev) * 100}%` }}
                />
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{p.qtySold} units sold</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentMixCard({
  storeId,
  date,
  currencyCode,
}: {
  storeId: number;
  date: string;
  currencyCode: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['pos-analytics-collection', storeId, date],
    queryFn: () => posApi.collectionReport(storeId, date, date),
  });

  const byMethod = (data?.byMethod ?? {}) as Record<string, number>;
  const entries = Object.entries(byMethod).sort((a, b) => b[1] - a[1]);
  const total = Number(data?.totalCollected) || entries.reduce((s, [, v]) => s + v, 0);

  const labels: Record<string, string> = {
    CASH: 'Cash',
    UPI: 'UPI',
    CARD: 'Card',
    WALLET: 'Wallet',
    RAZORPAY: 'Razorpay',
    STRIPE: 'Stripe',
  };

  return (
    <div className="card p-5">
      <h2 className="section-title">Payment methods</h2>
      {isLoading ? (
        <Skeleton className="mt-4 h-24 w-full" />
      ) : entries.length === 0 ? (
        <p className="mt-4 text-center text-xs text-slate-500">No payments recorded for this day</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {entries.map(([method, amount]) => {
            const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
            return (
              <li key={method}>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-700 dark:text-slate-300">{labels[method] ?? method}</span>
                  <span className="font-semibold tabular-nums text-primary-600 dark:text-primary-400">
                    {formatCurrency(amount, currencyCode)}
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div className="h-full rounded-full bg-primary-600" style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-0.5 text-[10px] text-slate-500">{pct}% of collected</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DateNav({ date, onChange }: { date: string; onChange: (d: string) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const prev = new Date(new Date(date).getTime() - 86_400_000).toISOString().slice(0, 10);
  const next = new Date(new Date(date).getTime() + 86_400_000).toISOString().slice(0, 10);
  const isToday = date === today;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(prev)}
        className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
        aria-label="Previous day"
      >
        ←
      </button>
      <DatePicker
        value={date}
        max={today}
        onChange={onChange}
        className="max-w-[10rem]"
      />
      <button
        type="button"
        onClick={() => onChange(next)}
        disabled={isToday}
        className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:hover:bg-slate-800"
        aria-label="Next day"
      >
        →
      </button>
      {!isToday && (
        <button
          type="button"
          onClick={() => onChange(today)}
          className="rounded-lg bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-600 hover:bg-primary-100 dark:bg-primary-950/40 dark:text-primary-400"
        >
          Today
        </button>
      )}
    </div>
  );
}

export function PosAnalyticsDashboard() {
  const store = useAppSelector((s) => s.app.selectedStore);
  const currencyCode = store?.currencyCode ?? 'INR';
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const storeId = store?.id;

  const { data: daily, isLoading: loadingDaily } = useQuery({
    queryKey: ['pos-analytics-daily', storeId, date],
    queryFn: () => posApi.getDailySummary(storeId!, date),
    enabled: !!storeId,
  });

  const { data: hourly = [], isLoading: loadingHourly } = useQuery({
    queryKey: ['pos-analytics-hourly', storeId, date],
    queryFn: () => posApi.getHourlySales(storeId!, date),
    enabled: !!storeId,
  });

  if (!store) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto text-sm text-slate-500">
        Select a store to view analytics.
      </div>
    );
  }

  const fmt = (v?: number) => formatCurrency(v ?? 0, currencyCode);

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="page-content p-4 sm:p-6 pb-8">
      <PageHeader
        title="POS Analytics"
        description={store.name}
        actions={<DateNav date={date} onChange={setDate} />}
      />

      <KpiGrid columns={4}>
        <StatCard
          title="Total sales"
          value={fmt(daily?.totalSales)}
          icon={<TrendingUpIcon />}
          trend={`${daily?.transactionCount ?? 0} transactions`}
        />
        <StatCard title="Transactions" value={daily?.transactionCount ?? 0} icon={<ReceiptIcon />} />
        <StatCard title="Avg basket" value={fmt(daily?.avgBasketSize)} icon={<ShoppingCartIcon />} />
        <StatCard
          title="Top product"
          value={daily?.topProducts?.[0]?.productName?.split(' ')[0] ?? '—'}
          icon={<StarIcon />}
          trend={daily?.topProducts?.[0] ? fmt(daily.topProducts[0].revenue) : undefined}
        />
      </KpiGrid>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {loadingHourly ? (
            <div className="card flex h-52 items-center justify-center p-5">
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          ) : (
            <HourlySalesChart data={hourly} currencyCode={currencyCode} />
          )}
        </div>
        <div>{storeId && <PaymentMixCard storeId={storeId} date={date} currencyCode={currencyCode} />}</div>
      </div>

      {loadingDaily ? (
        <div className="card flex h-40 items-center justify-center p-5">
          <Skeleton className="h-8 w-32" />
        </div>
      ) : (
        <TopProductsTable products={daily?.topProducts ?? []} currencyCode={currencyCode} />
      )}
      </div>
    </div>
  );
}
